import type { ReactNode } from 'react';

/** Every screen opens the same way: title, optional context line, actions at the far end (11 §3.1).
 *  `children` is the row under the title — tabs or filters — so screens stop improvising headers. */
export function PageHeader({
  title,
  meta,
  badge,
  actions,
  children,
}: {
  title: ReactNode;
  meta?: ReactNode;
  badge?: ReactNode;
  actions?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <div className="mb-4 flex flex-col gap-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-xl leading-tight font-semibold">{title}</h1>
            {badge}
          </div>
          {meta ? <div className="mt-1 text-sm text-text-muted">{meta}</div> : null}
        </div>
        {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
      </div>
      {children ? <div className="flex flex-wrap items-end gap-3">{children}</div> : null}
    </div>
  );
}

/** Bordered surface section. No shadow on cards (11 §6.1); header carries title + actions. */
export function Card({
  title,
  meta,
  actions,
  children,
  className = '',
  bodyClassName = 'p-4',
  tone = 'default',
}: {
  title?: ReactNode;
  meta?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
  bodyClassName?: string;
  tone?: 'default' | 'danger';
}) {
  return (
    <section
      className={`rounded-md border bg-surface ${tone === 'danger' ? 'border-danger/40' : 'border-border'} ${className}`}
    >
      {title || actions ? (
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-4 py-2.5">
          <div className="min-w-0">
            {title ? (
              <h2 className={`text-base font-semibold ${tone === 'danger' ? 'text-danger' : ''}`}>
                {title}
              </h2>
            ) : null}
            {meta ? <div className="text-xs text-text-muted">{meta}</div> : null}
          </div>
          {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
        </div>
      ) : null}
      <div className={bodyClassName}>{children}</div>
    </section>
  );
}

/** Keyboard hint, e.g. <Kbd>N</Kbd>. Hidden from the accessible name: shortcuts live in `aria-keyshortcuts`. */
export function Kbd({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <kbd
      aria-hidden="true"
      className={`num inline-flex h-5 min-w-5 items-center justify-center rounded-sm border border-current/30 px-1 font-sans text-[0.7rem] leading-none ${className}`}
    >
      {children}
    </kbd>
  );
}

/** Label / value pairs for detail headers (reservation, guest, account). */
export function DefinitionGrid({
  items,
  columns = 4,
}: {
  items: { label: ReactNode; value: ReactNode; ltr?: boolean }[];
  columns?: 2 | 3 | 4 | 5;
}) {
  const cols = {
    2: 'sm:grid-cols-2',
    3: 'sm:grid-cols-3',
    4: 'sm:grid-cols-4',
    5: 'sm:grid-cols-5',
  }[columns];
  return (
    <dl className={`grid grid-cols-1 gap-x-6 gap-y-3 ${cols}`}>
      {items.map((it, i) => (
        <div key={i} className="min-w-0">
          <dt className="text-xs text-text-muted">{it.label}</dt>
          {/* num goes on an inner span: on the dd it would flip text-start and pull LTR values to the far side */}
          <dd className="mt-0.5 truncate">
            {it.ltr ? <span className="num">{it.value}</span> : it.value}
          </dd>
        </div>
      ))}
    </dl>
  );
}

/** Segmented tab style shared by button tabs and router NavLinks (`className={({isActive}) => tabClass(isActive)}`). */
export function tabClass(active: boolean): string {
  return `inline-flex h-7 items-center gap-1.5 rounded-sm px-3 text-sm whitespace-nowrap transition-colors duration-100 ${
    active
      ? 'bg-surface font-semibold text-text ring-1 ring-border'
      : 'text-text-muted hover:text-text'
  }`;
}
export const tabListClass = 'inline-flex items-center gap-0.5 rounded-md bg-surface-muted p-0.5';

export function Tabs<K extends string>({
  items,
  value,
  onChange,
  label,
}: {
  items: { key: K; label: ReactNode; count?: number | undefined }[];
  value: K;
  onChange: (k: K) => void;
  label: string;
}) {
  return (
    <div role="group" aria-label={label} className={tabListClass}>
      {items.map((i) => (
        <button
          key={i.key}
          type="button"
          aria-pressed={i.key === value}
          onClick={() => onChange(i.key)}
          className={tabClass(i.key === value)}
        >
          {i.label}
          {i.count !== undefined ? (
            <span className="num text-xs text-text-muted">{i.count}</span>
          ) : null}
        </button>
      ))}
    </div>
  );
}

/** Sidebar link style shared by node-web, cloud-admin and cloud-portal (router-agnostic: pass to NavLink). */
export function navItemClass(active: boolean): string {
  return `flex h-8 items-center gap-2.5 rounded-md px-3 text-sm transition-colors duration-100 ${
    active ? 'bg-primary-soft font-semibold text-primary' : 'text-text hover:bg-surface-muted'
  }`;
}

/** Brand block at the top of a sidebar. */
export function SidebarBrand({ name, sub }: { name: string; sub?: string | undefined }) {
  return (
    <div className="flex h-12 shrink-0 items-center gap-2 border-b border-border px-4">
      <img src="/logo.png" alt="" width={28} height={28} className="h-7 w-7 shrink-0" />
      <span className="min-w-0">
        <span className="block truncate text-sm leading-tight font-semibold">{name}</span>
        {sub ? <span className="block truncate text-xs text-text-muted">{sub}</span> : null}
      </span>
    </div>
  );
}
